/**
 * The typed window slot (SA-1136; spec 2026-10-11-compass-demand-gap-matching-design §3, §6).
 * The reader twin of `backend/app/services/windows.py`, pinned by `shared/window-cases.json`,
 * plus the pure helpers `WindowInput` writes with. No React, no store, no fetch. The words are
 * the customer's: a person sets the dates and the flexibility, and types words only into a window
 * that had none when the editor opened (spec §6.3, ruling P40).
 */
import { isIsoDay, rangeWords } from './windowText'

export type Flexibility = 'fixed' | 'flexible' | 'asap' | 'unknown'
export type Basis = 'extracted' | 'parsed' | 'entered' | null
export type WindowState = 'dated' | 'guessed' | 'undated'
export interface WindowSlotValue {
  text: string
  earliest: string | null
  latest: string | null
  flexibility: Flexibility
  basis: Basis
}

export const FLEXIBILITIES: readonly Flexibility[] = ['fixed', 'flexible', 'asap', 'unknown']

const collapse = (s: unknown) => String(s).split(/\s+/).filter(Boolean).join(' ')

/** A mapping with a string `text` and BOTH date keys, and no place key (`code` / `codes`). */
export function isWindow(v: unknown): v is WindowSlotValue {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const o = v as Record<string, unknown>
  return typeof o.text === 'string' && 'earliest' in o && 'latest' in o && !('code' in o) && !('codes' in o)
}

export function windowDates(v: unknown): { earliest: string | null; latest: string | null } | null {
  if (!isWindow(v)) return null
  const earliest = v.earliest || null
  const latest = v.latest || null
  if (!earliest && !latest) return null
  // SA-1138: a date the calendar lacks reads as no dates, as `window_dates` does.
  if ((earliest && !isIsoDay(earliest)) || (latest && !isIsoDay(latest))) return null
  return { earliest, latest }
}

export function windowWords(v: unknown): string {
  if (v === null || v === undefined) return ''
  return collapse(isWindow(v) ? v.text : v)
}

export function windowState(v: unknown): WindowState {
  if (windowDates(v) === null) return 'undated'
  return (v as WindowSlotValue).basis === 'parsed' ? 'guessed' : 'dated'
}

export function windowFlexibility(v: unknown): Flexibility {
  return isWindow(v) && FLEXIBILITIES.includes(v.flexibility) ? v.flexibility : 'unknown'
}

export function windowKey(v: unknown): string {
  if (isWindow(v)) return `window|${v.text}|${v.earliest ?? ''}|${v.latest ?? ''}|${v.flexibility}|${v.basis ?? ''}`
  if (v === null || v === undefined) return ''
  return typeof v === 'object' ? JSON.stringify(v) : String(v)   // SA-1138: never "[object Object]"
}

/** A string as the undated window with that text; empty text is no window. */
export function legacyWindow(text: string): WindowSlotValue | null {
  const t = collapse(text)
  return t ? { text: t, earliest: null, latest: null, flexibility: 'unknown', basis: null } : null
}

/** The person sets the dates: ordered, and the window is theirs (`entered`); both cleared is undated.
 * Words follow the dates only when they are the dates' own words: there are none yet, or they are
 * `rangeWords` of the dates they replace AND a person set those dates (`entered`; SA-1138 ruling W6,
 * so a customer who wrote "14 Oct 2026" keeps those words). Clearing both dates of such a window
 * leaves no window (ruling P41); the customer's words stay as they are. */
export function withDates(prev: WindowSlotValue | null, earliest: string | null, latest: string | null): WindowSlotValue | null {
  let a = earliest || null
  let b = latest || null
  if (a && b && a > b) [a, b] = [b, a]
  const dated = Boolean(a || b)
  const own = collapse(prev?.text ?? '')
  const datesOnly = !own || (prev?.basis === 'entered' && own === rangeWords(prev.earliest, prev.latest))
  const text = datesOnly ? rangeWords(a, b) : own
  if (!text) return null
  return { text, earliest: a, latest: b, flexibility: prev?.flexibility ?? 'unknown', basis: dated ? 'entered' : null }
}

/** The person types the words (ruling P40: only into a window that had none when the editor opened).
 * Kept as typed; the server collapses them. New words over dates read from words (a guess, or the
 * model's `extracted` dates) clear those dates and their flexibility, so the server reads the new
 * words (P22; SA-1138 ruling W5); a person's own (`entered`) dates and flexibility stay. No words
 * and no dates is no window. */
export function withWords(prev: WindowSlotValue | null, text: string): WindowSlotValue | null {
  const fromWords = prev?.basis === 'parsed' || prev?.basis === 'extracted'
  const dated = !fromWords && windowDates(prev) !== null
  if (!text.trim() && !dated) return null
  if (!prev || fromWords) return { text, earliest: null, latest: null, flexibility: 'unknown', basis: null }
  return { ...prev, text }
}

/** The flexibility alone never moves the basis. */
export function withFlexibility(prev: WindowSlotValue | null, flexibility: Flexibility): WindowSlotValue | null {
  return prev ? { ...prev, flexibility } : null
}

/** Confirm a guessed window: the same dates, now the person's. */
export function confirmedWindow(prev: WindowSlotValue): WindowSlotValue {
  return { ...prev, basis: windowDates(prev) ? 'entered' : null }
}
